import { z } from 'zod'

export const BASIC_DATE_ANSWERS = ['known', 'unknown'] as const
export const BASIC_BUDGET_ANSWERS = ['known'] as const
export const BASIC_GUEST_COUNT_ANSWERS = ['known'] as const
export const BASIC_REGION_ANSWERS = ['known', 'unknown'] as const

export const BASIC_REGIONS = [
  'prague-central-bohemia',
  'south-bohemia',
  'west-bohemia',
  'north-bohemia',
  'east-bohemia',
  'vysocina',
  'south-moravia',
  'central-moravia',
  'north-moravia-silesia',
] as const

export const BASIC_CEREMONY_ANSWERS = ['civil', 'church', 'symbolic', 'unknown'] as const

export const LOCATION_COUNT_ANSWERS = ['single', 'split', 'unknown'] as const
export const LOCATION_VENUE_STATUS_ANSWERS = ['booked', 'unknown'] as const
export const LOCATION_VENUE_PRICE_ANSWERS = ['known', 'unknown'] as const
export const LOCATION_VENUE_STYLE_ANSWERS = [
  'elegant',
  'rustic',
  'garden',
  'hotel',
  'industrial',
  'restaurant',
  'unknown',
] as const
export const LOCATION_VENUE_COORDINATION_ANSWERS = ['supplier', 'own', 'unknown'] as const
export const LOCATION_VENUE_AVAILABILITY_ANSWERS = ['known', 'unknown'] as const
export const LOCATION_WEATHER_PLAN_ANSWERS = ['covered', 'tent', 'not-needed', 'unknown'] as const
export const GUESTS_TRANSPORT_ANSWERS = ['yes', 'no', 'unknown'] as const
export const GUESTS_TRANSPORT_PROVIDER_ANSWERS = ['driver', 'unknown'] as const
export const GUESTS_SUPPLIER_PRICE_ANSWERS = ['known', 'unknown'] as const
export const GUESTS_ARRIVAL_ANSWERS = ['known', 'unknown'] as const
export const GUESTS_ANNOUNCEMENT_TIMING_ANSWERS = [
  'twelve-months',
  'six-months',
  'unknown',
] as const
export const GUESTS_INVITATION_TIMING_ANSWERS = ['six-months', 'three-months', 'unknown'] as const
export const GUESTS_ANNOUNCEMENT_METHOD_ANSWERS = ['printed', 'online', 'unknown'] as const
export const GUESTS_INVITATION_METHOD_ANSWERS = ['printed', 'online', 'unknown'] as const
export const GUESTS_ONLINE_DELIVERY_ANSWERS = ['own', 'amore', 'unknown'] as const

const formText = z.string().trim().max(80).default('')
const optionalText = (maxLength: number) => z.string().trim().max(maxLength).default('')
const optionalTime = z
  .string()
  .trim()
  .regex(/^$|^\d{2}:\d{2}$/)
  .default('')

const venueAttachmentSchema = z.object({
  id: optionalText(120),
  name: optionalText(240),
  size: z.number().int().nonnegative().max(50_000_000).default(0),
  type: optionalText(120),
  dataUrl: z.string().max(5_000_000).default(''),
  uploadedAt: optionalText(80),
})

const guestGroupSchema = z.string().trim().min(1).max(120)
const driverContactSchema = z.object({
  id: optionalText(120),
  name: optionalText(120),
  phone: optionalText(80),
})
type GuestsDeliveryMethodValue = (typeof GUESTS_ANNOUNCEMENT_METHOD_ANSWERS)[number]
type GuestsOnlineDeliveryValue = (typeof GUESTS_ONLINE_DELIVERY_ANSWERS)[number] | ''
type SupplierPriceStatusValue = (typeof GUESTS_SUPPLIER_PRICE_ANSWERS)[number] | ''

const legacyBasicRegionToArea: Record<string, BasicRegion> = {
  prague: 'prague-central-bohemia',
  'central-bohemia': 'prague-central-bohemia',
  'south-bohemia': 'south-bohemia',
  pilsen: 'west-bohemia',
  'karlovy-vary': 'west-bohemia',
  usti: 'north-bohemia',
  liberec: 'north-bohemia',
  'hradec-kralove': 'east-bohemia',
  pardubice: 'east-bohemia',
  vysocina: 'vysocina',
  'south-moravia': 'south-moravia',
  olomouc: 'central-moravia',
  zlin: 'central-moravia',
  'moravian-silesian': 'north-moravia-silesia',
}

const basicRegionSchema = z.preprocess(
  (value) => (typeof value === 'string' ? (legacyBasicRegionToArea[value] ?? value) : value),
  z.union([z.enum(BASIC_REGIONS), z.literal('')]).default(''),
)

export const basicSetupDataSchema = z
  .object({
    dateAnswer: z.enum(BASIC_DATE_ANSWERS),
    weddingDate: formText,
    budgetAnswer: z.literal('known'),
    budgetAmount: formText,
    guestCountAnswer: z.literal('known'),
    guestCount: formText,
    regionAnswer: z.enum(BASIC_REGION_ANSWERS),
    region: basicRegionSchema,
    ceremony: z.enum(BASIC_CEREMONY_ANSWERS),
  })
  .superRefine((value, ctx) => {
    if (value.dateAnswer === 'known' && !z.iso.date().safeParse(value.weddingDate).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['weddingDate'],
        message: 'Pick the wedding date or choose unknown.',
      })
    }

    if (numberFromInput(value.budgetAmount) <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['budgetAmount'],
        message: 'Enter the budget estimate.',
      })
    }

    if (numberFromInput(value.guestCount) <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['guestCount'],
        message: 'Enter the guest count estimate.',
      })
    }

    if (value.regionAnswer === 'known' && value.region.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['region'],
        message: 'Pick the region or choose unknown.',
      })
    }
  })
  .transform((value) => ({
    ...value,
    weddingDate: value.dateAnswer === 'known' ? value.weddingDate : '',
    region: value.regionAnswer === 'known' ? value.region : '',
  }))

const locationVenueFieldsSchema = z.object({
  status: z.union([z.enum(LOCATION_VENUE_STATUS_ANSWERS), z.literal('')]).default(''),
  supplierId: optionalText(120),
  supplierName: optionalText(180),
  searchStyle: z.union([z.enum(LOCATION_VENUE_STYLE_ANSWERS), z.literal('')]).default(''),
  priceStatus: z.union([z.enum(LOCATION_VENUE_PRICE_ANSWERS), z.literal('')]).default(''),
  price: formText,
  agreement: optionalText(3000),
  notes: optionalText(3000),
  attachments: z.array(venueAttachmentSchema).max(20).default([]),
  availabilityStatus: z
    .union([z.enum(LOCATION_VENUE_AVAILABILITY_ANSWERS), z.literal('')])
    .default(''),
  availableFrom: optionalTime,
  availableTo: optionalTime,
  coordination: z.union([z.enum(LOCATION_VENUE_COORDINATION_ANSWERS), z.literal('')]).default(''),
  coordinationOwner: optionalText(180),
  coordinationNote: optionalText(1000),
  weatherPlan: z.union([z.enum(LOCATION_WEATHER_PLAN_ANSWERS), z.literal('')]).default(''),
  weatherPlanNote: optionalText(1000),
})

const emptyLocationVenueFields = {
  status: '',
  supplierId: '',
  supplierName: '',
  searchStyle: '',
  priceStatus: '',
  price: '',
  agreement: '',
  notes: '',
  attachments: [],
  availabilityStatus: '',
  availableFrom: '',
  availableTo: '',
  coordination: '',
  coordinationOwner: '',
  coordinationNote: '',
  weatherPlan: '',
  weatherPlanNote: '',
} satisfies z.infer<typeof locationVenueFieldsSchema>

export const locationSetupDataSchema = z
  .object({
    locationCount: z.enum(LOCATION_COUNT_ANSWERS),
    venue: locationVenueFieldsSchema,
    ceremonyVenue: locationVenueFieldsSchema,
    receptionVenue: locationVenueFieldsSchema,
    venueDeposit: formText,
    venuePaid: formText,
  })
  .superRefine((value, ctx) => {
    if (value.locationCount === 'single') {
      validateLocationVenue(value.venue, ctx, ['venue'])
    }

    if (value.locationCount === 'split') {
      validateLocationVenue(value.ceremonyVenue, ctx, ['ceremonyVenue'])
      validateLocationVenue(value.receptionVenue, ctx, ['receptionVenue'])
    }
  })
  .transform((value) => ({
    ...value,
    venue:
      value.locationCount === 'single'
        ? normalizeLocationVenue(value.venue)
        : emptyLocationVenueFields,
    ceremonyVenue:
      value.locationCount === 'split'
        ? normalizeLocationVenue(value.ceremonyVenue)
        : emptyLocationVenueFields,
    receptionVenue:
      value.locationCount === 'split'
        ? normalizeLocationVenue(value.receptionVenue)
        : emptyLocationVenueFields,
  }))

function migrateGuestsSetupData(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value
  }

  const data = { ...(value as Record<string, unknown>) }

  if (
    data.transportProviderAnswer === 'driver' &&
    (typeof data.transportSupplierId !== 'string' || data.transportSupplierId.trim().length === 0)
  ) {
    data.transportProviderAnswer = 'unknown'
    data.driverContacts = []
    data.driverName = ''
    data.driverContact = ''
    data.transportPriceStatus = ''
    data.transportPrice = ''
  }

  migrateGuestsDeliveryFields(data, {
    methodKey: 'announcementMethod',
    onlineMethodKey: 'announcementOnlineMethod',
    timingKey: 'announcementTiming',
    supplierIdKey: 'announcementSupplierId',
    supplierNameKey: 'announcementSupplierName',
    priceStatusKey: 'announcementPriceStatus',
    priceKey: 'announcementPrice',
  })
  migrateGuestsDeliveryFields(data, {
    methodKey: 'invitationMethod',
    onlineMethodKey: 'invitationOnlineMethod',
    timingKey: 'invitationTiming',
    supplierIdKey: 'invitationSupplierId',
    supplierNameKey: 'invitationSupplierName',
    priceStatusKey: 'invitationPriceStatus',
    priceKey: 'invitationPrice',
  })

  return data
}

function migrateGuestsDeliveryFields(
  data: Record<string, unknown>,
  keys: {
    methodKey: string
    onlineMethodKey: string
    timingKey: string
    supplierIdKey: string
    supplierNameKey: string
    priceStatusKey: string
    priceKey: string
  },
) {
  const method = data[keys.methodKey]

  if (method === 'app-website') {
    data[keys.methodKey] = 'online'
    data[keys.onlineMethodKey] = 'amore'
  }

  if (method === 'own') {
    data[keys.methodKey] = 'online'
    data[keys.onlineMethodKey] = 'own'
  }

  if (data[keys.methodKey] !== 'online') {
    data[keys.onlineMethodKey] = ''
  }

  const supplierId = data[keys.supplierIdKey]

  if (
    data[keys.methodKey] === 'printed' &&
    (typeof supplierId !== 'string' || supplierId.trim().length === 0)
  ) {
    data[keys.methodKey] = 'unknown'
    data[keys.onlineMethodKey] = ''
    data[keys.supplierIdKey] = ''
    data[keys.supplierNameKey] = ''
    data[keys.priceStatusKey] = ''
    data[keys.priceKey] = ''
  }

  const hasResolvedDelivery =
    data[keys.methodKey] === 'printed' ||
    (data[keys.methodKey] === 'online' &&
      (data[keys.onlineMethodKey] === 'own' || data[keys.onlineMethodKey] === 'amore'))

  const timing = data[keys.timingKey]

  if (hasResolvedDelivery) {
    data[keys.timingKey] = typeof timing === 'string' && timing.length > 0 ? timing : 'unknown'
  } else {
    data[keys.timingKey] = ''
  }

  if (data[keys.methodKey] !== 'printed') {
    data[keys.supplierIdKey] = ''
    data[keys.supplierNameKey] = ''
    data[keys.priceStatusKey] = ''
    data[keys.priceKey] = ''
  }
}

export const guestsSetupDataSchema = z.preprocess(
  migrateGuestsSetupData,
  z
    .object({
      guestGroups: z.array(guestGroupSchema).max(20).default([]),
      transportAnswer: z.enum(GUESTS_TRANSPORT_ANSWERS),
      transportProviderAnswer: z
        .union([z.enum(GUESTS_TRANSPORT_PROVIDER_ANSWERS), z.literal('')])
        .default(''),
      transportSupplierId: optionalText(120),
      transportSupplierName: optionalText(180),
      driverContacts: z.array(driverContactSchema).max(10).default([]),
      transportPriceStatus: z
        .union([z.enum(GUESTS_SUPPLIER_PRICE_ANSWERS), z.literal('')])
        .default('unknown'),
      transportPrice: formText,
      // Legacy fields from the first Hosté draft. Keep them readable so saved
      // work can be normalized into the new multi-contact structure.
      driverName: optionalText(180),
      driverContact: optionalText(180),
      guestArrivalAnswer: z.enum(GUESTS_ARRIVAL_ANSWERS),
      guestArrivalFrom: optionalTime,
      guestArrivalBy: optionalTime,
      announcementMethod: z.enum(GUESTS_ANNOUNCEMENT_METHOD_ANSWERS),
      announcementOnlineMethod: z
        .union([z.enum(GUESTS_ONLINE_DELIVERY_ANSWERS), z.literal('')])
        .default(''),
      announcementTiming: z
        .union([z.enum(GUESTS_ANNOUNCEMENT_TIMING_ANSWERS), z.literal('')])
        .default(''),
      announcementSupplierId: optionalText(120),
      announcementSupplierName: optionalText(180),
      announcementPriceStatus: z
        .union([z.enum(GUESTS_SUPPLIER_PRICE_ANSWERS), z.literal('')])
        .default('unknown'),
      announcementPrice: formText,
      invitationMethod: z.enum(GUESTS_INVITATION_METHOD_ANSWERS),
      invitationOnlineMethod: z
        .union([z.enum(GUESTS_ONLINE_DELIVERY_ANSWERS), z.literal('')])
        .default(''),
      invitationTiming: z
        .union([z.enum(GUESTS_INVITATION_TIMING_ANSWERS), z.literal('')])
        .default(''),
      invitationSupplierId: optionalText(120),
      invitationSupplierName: optionalText(180),
      invitationPriceStatus: z
        .union([z.enum(GUESTS_SUPPLIER_PRICE_ANSWERS), z.literal('')])
        .default('unknown'),
      invitationPrice: formText,
    })
    .superRefine((value, ctx) => {
      if (value.guestGroups.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['guestGroups'],
          message: 'Pick at least one guest group.',
        })
      }

      if (value.transportAnswer === 'yes') {
        const driverContacts = normalizeDriverContacts(value)

        if (value.transportProviderAnswer.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['transportProviderAnswer'],
            message: 'Choose who will provide guest transport.',
          })
        }

        if (value.transportProviderAnswer === 'driver') {
          if (value.transportSupplierId.length === 0) {
            ctx.addIssue({
              code: 'custom',
              path: ['transportSupplierId'],
              message: 'Pick the transport supplier.',
            })
          }

          if (driverContacts.length === 0) {
            ctx.addIssue({
              code: 'custom',
              path: ['driverContacts'],
              message: 'Enter at least one driver contact.',
            })
          }

          driverContacts.forEach((contact, index) => {
            if (contact.name.length === 0) {
              ctx.addIssue({
                code: 'custom',
                path: ['driverContacts', index, 'name'],
                message: 'Enter driver name.',
              })
            }

            if (contact.phone.length === 0) {
              ctx.addIssue({
                code: 'custom',
                path: ['driverContacts', index, 'phone'],
                message: 'Enter driver phone.',
              })
            }
          })

          validateSupplierPrice(value.transportPriceStatus, value.transportPrice, ctx, [
            'transportPrice',
          ])
        }
      }

      if (value.guestArrivalAnswer === 'known') {
        if (value.guestArrivalFrom.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['guestArrivalFrom'],
            message: 'Enter guest arrival start.',
          })
        }

        if (value.guestArrivalBy.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['guestArrivalBy'],
            message: 'Enter guest arrival end.',
          })
        }

        if (
          value.guestArrivalFrom.length > 0 &&
          value.guestArrivalBy.length > 0 &&
          !isValidGuestArrivalRange(value.guestArrivalFrom, value.guestArrivalBy)
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['guestArrivalBy'],
            message: 'Enter a valid guest arrival range.',
          })
        }
      }

      validateGuestsDelivery({
        method: value.announcementMethod,
        onlineMethod: value.announcementOnlineMethod,
        timing: value.announcementTiming,
        supplierId: value.announcementSupplierId,
        priceStatus: value.announcementPriceStatus,
        price: value.announcementPrice,
        ctx,
        onlinePath: ['announcementOnlineMethod'],
        timingPath: ['announcementTiming'],
        supplierPath: ['announcementSupplierId'],
        pricePath: ['announcementPrice'],
      })

      validateGuestsDelivery({
        method: value.invitationMethod,
        onlineMethod: value.invitationOnlineMethod,
        timing: value.invitationTiming,
        supplierId: value.invitationSupplierId,
        priceStatus: value.invitationPriceStatus,
        price: value.invitationPrice,
        ctx,
        onlinePath: ['invitationOnlineMethod'],
        timingPath: ['invitationTiming'],
        supplierPath: ['invitationSupplierId'],
        pricePath: ['invitationPrice'],
      })
    })
    .transform(({ driverName: _driverName, driverContact: _driverContact, ...value }) => {
      const hasTransportSupplier =
        value.transportAnswer === 'yes' && value.transportProviderAnswer === 'driver'
      const hasAnnouncementSupplier = value.announcementMethod === 'printed'
      const hasInvitationSupplier = value.invitationMethod === 'printed'

      return {
        ...value,
        transportProviderAnswer:
          value.transportAnswer === 'yes' ? value.transportProviderAnswer : '',
        transportSupplierId: hasTransportSupplier ? value.transportSupplierId : '',
        transportSupplierName: hasTransportSupplier ? value.transportSupplierName : '',
        driverContacts: hasTransportSupplier
          ? normalizeDriverContacts({
              ...value,
              driverName: _driverName,
              driverContact: _driverContact,
            })
          : [],
        transportPriceStatus: hasTransportSupplier ? value.transportPriceStatus : '',
        transportPrice:
          hasTransportSupplier && value.transportPriceStatus === 'known'
            ? value.transportPrice
            : '',
        guestArrivalFrom: value.guestArrivalAnswer === 'known' ? value.guestArrivalFrom : '',
        guestArrivalBy: value.guestArrivalAnswer === 'known' ? value.guestArrivalBy : '',
        announcementOnlineMethod:
          value.announcementMethod === 'online' ? value.announcementOnlineMethod : '',
        announcementTiming: isResolvedGuestsDelivery(
          value.announcementMethod,
          value.announcementOnlineMethod,
        )
          ? value.announcementTiming
          : '',
        announcementSupplierId: hasAnnouncementSupplier ? value.announcementSupplierId : '',
        announcementSupplierName: hasAnnouncementSupplier ? value.announcementSupplierName : '',
        announcementPriceStatus: hasAnnouncementSupplier ? value.announcementPriceStatus : '',
        announcementPrice:
          hasAnnouncementSupplier && value.announcementPriceStatus === 'known'
            ? value.announcementPrice
            : '',
        invitationOnlineMethod:
          value.invitationMethod === 'online' ? value.invitationOnlineMethod : '',
        invitationTiming: isResolvedGuestsDelivery(
          value.invitationMethod,
          value.invitationOnlineMethod,
        )
          ? value.invitationTiming
          : '',
        invitationSupplierId: hasInvitationSupplier ? value.invitationSupplierId : '',
        invitationSupplierName: hasInvitationSupplier ? value.invitationSupplierName : '',
        invitationPriceStatus: hasInvitationSupplier ? value.invitationPriceStatus : '',
        invitationPrice:
          hasInvitationSupplier && value.invitationPriceStatus === 'known'
            ? value.invitationPrice
            : '',
      }
    }),
)

export type BasicDateAnswer = (typeof BASIC_DATE_ANSWERS)[number]
export type BasicBudgetAnswer = (typeof BASIC_BUDGET_ANSWERS)[number]
export type BasicGuestCountAnswer = (typeof BASIC_GUEST_COUNT_ANSWERS)[number]
export type BasicRegionAnswer = (typeof BASIC_REGION_ANSWERS)[number]
export type BasicRegion = (typeof BASIC_REGIONS)[number]
export type BasicCeremonyAnswer = (typeof BASIC_CEREMONY_ANSWERS)[number]
export type BasicSetupData = z.infer<typeof basicSetupDataSchema>
export type LocationCountAnswer = (typeof LOCATION_COUNT_ANSWERS)[number]
export type LocationVenueStatusAnswer = (typeof LOCATION_VENUE_STATUS_ANSWERS)[number]
export type LocationVenuePriceAnswer = (typeof LOCATION_VENUE_PRICE_ANSWERS)[number]
export type LocationVenueStyleAnswer = (typeof LOCATION_VENUE_STYLE_ANSWERS)[number]
export type LocationVenueCoordinationAnswer = (typeof LOCATION_VENUE_COORDINATION_ANSWERS)[number]
export type LocationVenueAvailabilityAnswer = (typeof LOCATION_VENUE_AVAILABILITY_ANSWERS)[number]
export type LocationWeatherPlanAnswer = (typeof LOCATION_WEATHER_PLAN_ANSWERS)[number]
export type LocationVenueAttachment = z.infer<typeof venueAttachmentSchema>
export type LocationVenueData = z.infer<typeof locationVenueFieldsSchema>
export type LocationSetupData = z.infer<typeof locationSetupDataSchema>
export type GuestsTransportAnswer = (typeof GUESTS_TRANSPORT_ANSWERS)[number]
export type GuestsTransportProviderAnswer = (typeof GUESTS_TRANSPORT_PROVIDER_ANSWERS)[number]
export type GuestsSupplierPriceAnswer = (typeof GUESTS_SUPPLIER_PRICE_ANSWERS)[number]
export type GuestsArrivalAnswer = (typeof GUESTS_ARRIVAL_ANSWERS)[number]
export type GuestsAnnouncementTimingAnswer = (typeof GUESTS_ANNOUNCEMENT_TIMING_ANSWERS)[number]
export type GuestsAnnouncementMethodAnswer = (typeof GUESTS_ANNOUNCEMENT_METHOD_ANSWERS)[number]
export type GuestsOnlineDeliveryAnswer = (typeof GUESTS_ONLINE_DELIVERY_ANSWERS)[number]
export type GuestsInvitationTimingAnswer = (typeof GUESTS_INVITATION_TIMING_ANSWERS)[number]
export type GuestsInvitationMethodAnswer = (typeof GUESTS_INVITATION_METHOD_ANSWERS)[number]
export type GuestsDriverContact = z.infer<typeof driverContactSchema>
export type GuestsSetupData = z.infer<typeof guestsSetupDataSchema>

function validateLocationVenue(
  venue: z.infer<typeof locationVenueFieldsSchema>,
  ctx: z.RefinementCtx,
  path: string[],
) {
  if (venue.status.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'status'],
      message: 'Choose venue status.',
    })
    return
  }

  if (venue.status !== 'booked') {
    return
  }

  if (venue.supplierId.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'supplierId'],
      message: 'Pick the venue supplier.',
    })
  }

  if (venue.priceStatus.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'priceStatus'],
      message: 'Choose venue price status.',
    })
  }

  if (venue.priceStatus === 'known' && numberFromInput(venue.price) <= 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'price'],
      message: 'Enter the venue price.',
    })
  }

  if (venue.coordination.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'coordination'],
      message: 'Choose venue coordination.',
    })
  }

  if (venue.coordination === 'own' && venue.coordinationOwner.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'coordinationOwner'],
      message: 'Enter the coordination owner.',
    })
  }

  const availabilityStatus = getNormalizedVenueAvailabilityStatus(venue)

  if (availabilityStatus.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'availabilityStatus'],
      message: 'Choose venue availability status.',
    })
  }

  if (availabilityStatus === 'known' && venue.availableFrom.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'availableFrom'],
      message: 'Enter venue availability start.',
    })
  }

  if (availabilityStatus === 'known' && venue.availableTo.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'availableTo'],
      message: 'Enter venue availability end.',
    })
  }

  if (venue.weatherPlan.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'weatherPlan'],
      message: 'Choose weather plan.',
    })
  }
}

function normalizeLocationVenue(venue: z.infer<typeof locationVenueFieldsSchema>) {
  if (venue.status !== 'booked') {
    return {
      ...emptyLocationVenueFields,
      status: venue.status,
    }
  }

  const availabilityStatus = getNormalizedVenueAvailabilityStatus(venue)

  return {
    ...venue,
    price: venue.priceStatus === 'known' ? venue.price : '',
    availabilityStatus,
    availableFrom: availabilityStatus === 'known' ? venue.availableFrom : '',
    availableTo: availabilityStatus === 'known' ? venue.availableTo : '',
    coordinationOwner: venue.coordination === 'own' ? venue.coordinationOwner : '',
  }
}

function getNormalizedVenueAvailabilityStatus(venue: z.infer<typeof locationVenueFieldsSchema>) {
  if (venue.availabilityStatus.length > 0) {
    return venue.availabilityStatus
  }

  return venue.availableFrom.length > 0 && venue.availableTo.length > 0 ? 'known' : ''
}

function normalizeDriverContacts(value: {
  driverContacts: z.infer<typeof driverContactSchema>[]
  driverName?: string
  driverContact?: string
}) {
  const contacts = value.driverContacts
    .map((contact) => ({
      id: contact.id,
      name: contact.name.trim(),
      phone: contact.phone.trim(),
    }))
    .filter((contact) => contact.name.length > 0 || contact.phone.length > 0)

  if (contacts.length > 0) {
    return contacts
  }

  const legacyName = value.driverName?.trim() ?? ''
  const legacyPhone = value.driverContact?.trim() ?? ''

  if (legacyName.length === 0 && legacyPhone.length === 0) {
    return []
  }

  return [
    {
      id: '',
      name: legacyName,
      phone: legacyPhone,
    },
  ]
}

function validateGuestsDelivery({
  method,
  onlineMethod,
  timing,
  supplierId,
  priceStatus,
  price,
  ctx,
  onlinePath,
  timingPath,
  supplierPath,
  pricePath,
}: {
  method: GuestsDeliveryMethodValue
  onlineMethod: GuestsOnlineDeliveryValue
  timing: string
  supplierId: string
  priceStatus: SupplierPriceStatusValue
  price: string
  ctx: z.RefinementCtx
  onlinePath: string[]
  timingPath: string[]
  supplierPath: string[]
  pricePath: string[]
}) {
  if (method === 'online' && onlineMethod.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: onlinePath,
      message: 'Choose online delivery.',
    })
  }

  if (isResolvedGuestsDelivery(method, onlineMethod) && timing.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: timingPath,
      message: 'Choose delivery timing.',
    })
  }

  if (method !== 'printed') {
    return
  }

  if (supplierId.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: supplierPath,
      message: 'Pick the stationery supplier.',
    })
  }

  validateSupplierPrice(priceStatus, price, ctx, pricePath)
}

function isResolvedGuestsDelivery(
  method: GuestsDeliveryMethodValue,
  onlineMethod: GuestsOnlineDeliveryValue,
) {
  return (
    method === 'printed' ||
    (method === 'online' && onlineMethod.length > 0 && onlineMethod !== 'unknown')
  )
}

function validateSupplierPrice(
  priceStatus: SupplierPriceStatusValue,
  price: string,
  ctx: z.RefinementCtx,
  path: string[],
) {
  if (priceStatus.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path,
      message: 'Choose supplier price status.',
    })
  }

  if (priceStatus === 'known' && numberFromInput(price) <= 0) {
    ctx.addIssue({
      code: 'custom',
      path,
      message: 'Enter supplier price.',
    })
  }
}

function isValidGuestArrivalRange(start: string, end: string) {
  const startMinute = timeToMinutes(start)
  const endMinute = timeToMinutes(end)

  if (startMinute === null || endMinute === null) {
    return false
  }

  const adjustedEndMinute = endMinute <= startMinute ? endMinute + 1440 : endMinute

  return adjustedEndMinute <= 1800
}

function timeToMinutes(value: string) {
  const match = /^(\d{2}):(\d{2})$/.exec(value)

  if (!match) {
    return null
  }

  const hours = Number.parseInt(match[1] ?? '', 10)
  const minutes = Number.parseInt(match[2] ?? '', 10)

  if (hours > 23 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

function numberFromInput(value: string) {
  const parsed = Number.parseInt(value.replace(/\D/g, ''), 10)
  return Number.isNaN(parsed) ? 0 : parsed
}
